/**
 * IT Audit Log Panel Component
 * View and filter audit logs for IT module actions
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, 
  Search, 
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Clock,
  User,
  Activity,
  AlertTriangle,
  Package,
  Ticket,
  Wrench,
  FileText,
  Megaphone,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';
import ITPagination from '../it/ITPagination';
import { formatDateTimeLocal } from '../../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('token');

// Action Icon Mapping
const getActionIcon = (action) => {
  if (action.includes('create')) return { icon: '➕', color: '#4ade80' };
  if (action.includes('update')) return { icon: '✏️', color: '#60a5fa' };
  if (action.includes('delete') || action.includes('retire')) return { icon: '🗑️', color: '#f87171' };
  if (action.includes('assign')) return { icon: '👤', color: '#a78bfa' };
  if (action.includes('return')) return { icon: '📦', color: '#fbbf24' };
  if (action.includes('maintenance')) return { icon: '🔧', color: '#f59e0b' };
  if (action.includes('escalate')) return { icon: '⬆️', color: '#ef4444' };
  if (action.includes('resolve') || action.includes('close')) return { icon: '✅', color: '#22c55e' };
  if (action.includes('export')) return { icon: '📤', color: '#06b6d4' };
  return { icon: '📋', color: '#64748b' };
};

// Module Icon Mapping
const getModuleIcon = (module) => {
  const icons = {
    assets: Package,
    tickets: Ticket,
    tasks: Activity,
    maintenance: Wrench,
    announcements: Megaphone,
    reminders: Bell,
    users: User
  };
  return icons[module] || FileText;
};

// Audit Log Entry Component
const AuditLogEntry = ({ log, expanded, onToggle }) => {
  const actionInfo = getActionIcon(log.action);
  
  // Get the module icon component - use useMemo pattern to avoid creating during render
  const moduleIcons = {
    assets: Package,
    tickets: Ticket,
    tasks: Activity,
    maintenance: Wrench,
    announcements: Megaphone,
    reminders: Bell,
    users: User
  };
  const ModuleIcon = moduleIcons[log.module] || FileText;
  
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    
    return formatDateTimeLocal(date);
  };
  
  const formatAction = (action) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };
  
  return (
    <div className={`audit-log-entry ${expanded ? 'expanded' : ''}`}>
      <div className="log-main" onClick={onToggle}>
        <div className="log-icon" style={{ background: `${actionInfo.color}20` }}>
          <span>{actionInfo.icon}</span>
        </div>
        
        <div className="log-content">
          <div className="log-header">
            <span className="log-action">{formatAction(log.action)}</span>
            <span className={`log-module module-${log.module}`}>
              <ModuleIcon size={12} /> {log.module}
            </span>
          </div>
          <div className="log-meta">
            <span className="log-user">
              <User size={12} /> {log.performed_by_name}
            </span>
            {log.resource_id && (
              <span className="log-resource">
                ID: {log.resource_id.slice(0, 8)}...
              </span>
            )}
          </div>
        </div>
        
        <div className="log-time">
          <Clock size={12} />
          {formatTimestamp(log.timestamp)}
        </div>
        
        <ChevronRight 
          size={16} 
          className={`expand-icon ${expanded ? 'rotated' : ''}`} 
        />
      </div>
      
      {expanded && (
        <div className="log-details">
          <div className="detail-grid">
            <div className="detail-item">
              <span className="label">Full Timestamp</span>
              <span className="value">{formatDateTimeLocal(log.timestamp)}</span>
            </div>
            <div className="detail-item">
              <span className="label">User Role</span>
              <span className="value">{log.performed_by_role || 'N/A'}</span>
            </div>
            {log.ip_address && (
              <div className="detail-item">
                <span className="label">IP Address</span>
                <span className="value mono">{log.ip_address}</span>
              </div>
            )}
            {log.resource_id && (
              <div className="detail-item">
                <span className="label">Resource ID</span>
                <span className="value mono">{log.resource_id}</span>
              </div>
            )}
          </div>
          
          {log.details && Object.keys(log.details).length > 0 && (
            <div className="detail-section">
              <h5>Action Details</h5>
              <div className="detail-json">
                {Object.entries(log.details).map(([key, value]) => (
                  <div key={key} className="json-item">
                    <span className="json-key">{key}:</span>
                    <span className="json-value">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {log.changes && Object.keys(log.changes).length > 0 && (
            <div className="detail-section">
              <h5>Changes Made</h5>
              <div className="changes-list">
                {Object.entries(log.changes).map(([field, change]) => (
                  <div key={field} className="change-item">
                    <span className="field-name">{field}</span>
                    <span className="old-value">{change.old || '(empty)'}</span>
                    <span className="arrow">→</span>
                    <span className="new-value">{change.new || '(empty)'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Audit Summary Card
const AuditSummaryCard = ({ summary }) => {
  if (!summary) return null;
  
  return (
    <div className="audit-summary">
      <div className="summary-stat total">
        <Activity size={20} />
        <div className="stat-info">
          <span className="stat-value">{summary.total_actions}</span>
          <span className="stat-label">Total Actions</span>
        </div>
      </div>
      
      <div className="summary-breakdown">
        <h5>By Module</h5>
        <div className="breakdown-items">
          {Object.entries(summary.by_module || {}).slice(0, 5).map(([module, count]) => {
            const ModuleIcon = getModuleIcon(module);
            return (
              <div key={module} className="breakdown-item">
                <ModuleIcon size={14} />
                <span className="name">{module}</span>
                <span className="count">{count}</span>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="summary-breakdown">
        <h5>Top Users</h5>
        <div className="breakdown-items">
          {(summary.by_user || []).slice(0, 3).map((user, idx) => (
            <div key={idx} className="breakdown-item">
              <User size={14} />
              <span className="name">{user.name}</span>
              <span className="count">{user.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main Audit Log Panel Component
const AuditLogPanel = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLog, setExpandedLog] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    module: '',
    days: 7,
    search: ''
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0
  });
  
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });
      
      if (filters.module) params.append('module', filters.module);
      
      // Calculate date range
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - filters.days);
      params.append('start_date', startDate.toISOString());
      
      const response = await fetch(`${API_URL}/api/it-audit/logs?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setPagination(prev => ({
          ...prev,
          total: data.total,
          pages: data.pages
        }));
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters.module, filters.days]);
  
  const fetchSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/it-audit/logs/summary?days=${filters.days}`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Failed to fetch audit summary:', error);
    }
  }, [filters.days]);
  
  useEffect(() => {
    fetchLogs();
    fetchSummary();
  }, [fetchLogs, fetchSummary]);
  
  const handleRefresh = () => {
    fetchLogs();
    fetchSummary();
  };
  
  // Filter logs by search term
  const filteredLogs = logs.filter(log => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      log.action?.toLowerCase().includes(search) ||
      log.performed_by_name?.toLowerCase().includes(search) ||
      log.resource_id?.toLowerCase().includes(search)
    );
  });
  
  return (
    <div className="audit-log-panel">
      <div className="panel-header">
        <div className="header-left">
          <Shield size={20} />
          <h3>Audit Log</h3>
        </div>
        <div className="header-actions">
          <button className="btn-refresh" onClick={handleRefresh}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>
      
      <AuditSummaryCard summary={summary} />
      
      <div className="audit-filters">
        <div className="filter-group">
          <label>Module</label>
          <select 
            value={filters.module} 
            onChange={e => {
              setFilters({...filters, module: e.target.value});
              setPagination(prev => ({...prev, page: 1}));
            }}
          >
            <option value="">All Modules</option>
            <option value="assets">Assets</option>
            <option value="tickets">Tickets</option>
            <option value="tasks">Tasks</option>
            <option value="maintenance">Maintenance</option>
            <option value="announcements">Announcements</option>
            <option value="reminders">Reminders</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Period</label>
          <select 
            value={filters.days} 
            onChange={e => {
              setFilters({...filters, days: parseInt(e.target.value)});
              setPagination(prev => ({...prev, page: 1}));
            }}
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        
        <div className="filter-group search">
          <Search size={14} />
          <input 
            type="text"
            placeholder="Search logs..."
            value={filters.search}
            onChange={e => setFilters({...filters, search: e.target.value})}
          />
        </div>
      </div>
      
      <div className="audit-logs-list">
        {loading ? (
          <div className="loading-state">Loading audit logs...</div>
        ) : filteredLogs.length === 0 ? (
          <div className="empty-state">
            <Shield size={32} />
            <p>No audit logs found</p>
            <span>Try adjusting your filters</span>
          </div>
        ) : (
          <>
            {filteredLogs.map(log => (
              <AuditLogEntry 
                key={log.id}
                log={log}
                expanded={expandedLog === log.id}
                onToggle={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
              />
            ))}
            
            {pagination.total > 0 && (
              <ITPagination
                page={pagination.page}
                limit={pagination.limit}
                total={pagination.total}
                onPageChange={(p) => setPagination(prev => ({ ...prev, page: p }))}
                onLimitChange={(l) => setPagination(prev => ({ ...prev, limit: l, page: 1 }))}
                testid="audit-pagination"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogPanel;
